import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import type {
  AutoMatchResult,
  CompletePendingStaffPayload,
  ListWorkplaceEmployeesQuery,
  MapStaffPayload,
  PendingStaffListResult,
  StaffMappingListResult,
  SyncNowPayload,
  SyncResult,
  TestConnectionResult,
  UpsertWorkplaceConnectionPayload,
  WorkplaceBranch,
  WorkplaceConnection,
  WorkplaceEmployee,
} from './model'

/**
 * Workplace ("Partner") integration service.
 *
 * Follows the repo's service-factory convention (a function taking the shared
 * axios client and returning an object of async methods). The axios client
 * already attaches the Firebase token, `x-tenant-id` and `branch-id` headers,
 * so callers do not deal with auth here.
 *
 * Backend contract: quantum-api admin endpoints from the integration plan
 * (Part C3). These payloads are flat JSON, so we return them directly without
 * ts-jackson deserialization (consistent with how other flat-shape endpoints
 * such as branches/is-multi-branch are handled).
 */
export const WorkplaceService = (client: AxiosInstance) => {
  return {
    /** GET /workplace-connection — current connection (secrets masked). */
    async getConnection(): Promise<WorkplaceConnection | null> {
      const { data } = await wrapApiCall(() =>
        client.get<WorkplaceConnection | null>('/workplace-connection')
      )
      return data ?? null
    },

    /** PUT /workplace-connection — create or update the connection. */
    async upsertConnection(
      payload: UpsertWorkplaceConnectionPayload
    ): Promise<WorkplaceConnection> {
      const { data } = await wrapApiCall(() =>
        client.put<WorkplaceConnection>('/workplace-connection', payload)
      )
      return data
    },

    /** POST /workplace-connection/test — verify credentials against Workplace. */
    async testConnection(): Promise<TestConnectionResult> {
      const { data } = await wrapApiCall(() =>
        client.post<TestConnectionResult>('/workplace-connection/test')
      )
      return data
    },

    /**
     * GET /workplace-connection/employees — fuzzy search Workplace employees by
     * email/name. Used by the staff-mapping table's per-row picker.
     */
    async listWorkplaceEmployees(
      query: ListWorkplaceEmployeesQuery = {}
    ): Promise<WorkplaceEmployee[]> {
      const { data } = await wrapApiCall(() =>
        client.get<WorkplaceEmployee[] | { data: WorkplaceEmployee[] }>(
          '/workplace-connection/employees',
          {
            params: {
              ...(query.search ? { search: query.search } : {}),
              ...(query.page ? { page: query.page } : {}),
              ...(query.pageSize ? { pageSize: query.pageSize } : {}),
            },
          }
        )
      )
      // Tolerate either a bare array or a { data: [...] } envelope — the exact
      // shape is not pinned in the plan, so accept both defensively.
      if (Array.isArray(data)) return data
      return data?.data ?? []
    },

    /** GET /workplace-connection/branches — list Workplace branches for mapping. */
    async listWorkplaceBranches(): Promise<WorkplaceBranch[]> {
      const { data } = await wrapApiCall(() =>
        client.get<WorkplaceBranch[] | { data: WorkplaceBranch[] }>(
          '/workplace-connection/branches'
        )
      )
      if (Array.isArray(data)) return data
      return data?.data ?? []
    },

    /**
     * GET /workplace-connection/staff — paginated FitUP staff for mapping.
     * Defaults to only-unmapped (pass unmappedOnly:false to include mapped).
     */
    async listStaffForMapping(
      query: { page?: number; pageSize?: number; unmappedOnly?: boolean } = {}
    ): Promise<StaffMappingListResult> {
      const { data } = await wrapApiCall(() =>
        client.get<StaffMappingListResult>('/workplace-connection/staff', {
          params: {
            page: query.page ?? 1,
            pageSize: query.pageSize ?? 10,
            ...(query.unmappedOnly === false ? { unmappedOnly: 'false' } : {}),
          },
        })
      )
      return data
    },

    /** POST /workplace-connection/staff-mapping/auto-match — match by email. */
    async autoMatchStaff(): Promise<AutoMatchResult> {
      const { data } = await wrapApiCall(() =>
        client.post<AutoMatchResult>('/workplace-connection/staff-mapping/auto-match')
      )
      return data
    },

    /** POST /workplace-connection/staff-mapping — persist FitUP↔Workplace links. */
    async mapStaff(payload: MapStaffPayload): Promise<void> {
      await wrapApiCall(() => client.post('/workplace-connection/staff-mapping', payload))
    },

    /** POST /workplace-connection/sync — push commission/conduct for a month. */
    async syncNow(payload: SyncNowPayload = {}): Promise<SyncResult> {
      const { data } = await wrapApiCall(() =>
        client.post<SyncResult>('/workplace-connection/sync', payload)
      )
      return data
    },

    /**
     * GET /workplace-connection/pending-staff — paginated FitUP users that were
     * auto-provisioned from Workplace and still need their profile completed
     * (missing role / citizenId / branch).
     */
    async listPendingStaff(
      query: { page?: number; pageSize?: number } = {}
    ): Promise<PendingStaffListResult> {
      const { data } = await wrapApiCall(() =>
        client.get<PendingStaffListResult>('/workplace-connection/pending-staff', {
          params: {
            page: query.page ?? 1,
            pageSize: query.pageSize ?? 10,
          },
        })
      )
      return data
    },

    /**
     * POST /workplace-connection/pending-staff/:id/complete — fill in the
     * missing role / citizenId / branch so the user is no longer pending.
     */
    async completePendingStaff(id: number, payload: CompletePendingStaffPayload): Promise<void> {
      await wrapApiCall(() =>
        client.post(`/workplace-connection/pending-staff/${id}/complete`, payload)
      )
    },
  }
}
